// @flow
import type { State } from "@store/reducers";
import type { DepartmentSpecificScript } from "@modules/service/reducer/department-specific-scripts/types";
import { ApprovalStateEnum } from "@modules/service/constants";
import type { HierarchyAssignment, ApprovableContainer } from "@modules/service/types";
import { validateHierarchyAssignmentSchema } from "./index";

const { DRAFT_ONLY, ACTIVE_WITH_DRAFT } = ApprovalStateEnum;

export type GetDepartmentSpecificScriptsList = (state: State) => Array<DepartmentSpecificScript>;

export const getDepartmentSpecificScriptsList = (
  state: State,
): Array<ApprovableContainer<DepartmentSpecificScript>> =>
  (state.service.departmentSpecificScripts.list || {}).content || [];

export const getDepartmentSpecificScriptsActiveList: GetDepartmentSpecificScriptsList = (
  state: State,
) => {
  return getDepartmentSpecificScriptsList(state)
    .filter(c => c.approvalState.indexOf("ACTIVE_") !== -1)
    .reduce((acc, c) => (c.active ? [...acc, c.active] : acc), []);
};

export const getDepartmentSpecificScriptsPendingList: GetDepartmentSpecificScriptsList = (
  state: State,
) => {
  return getDepartmentSpecificScriptsList(state)
    .filter(c => c.approvalState.indexOf("PENDING_REVIEW") !== -1)
    .reduce((acc, c) => (c.draft ? [...acc, c.draft] : acc), []);
};

export const getDepartmentSpecificScriptsDraftList: GetDepartmentSpecificScriptsList = (
  state: State,
) => {
  return getDepartmentSpecificScriptsList(state)
    .filter(c => c.approvalState === DRAFT_ONLY || c.approvalState === ACTIVE_WITH_DRAFT)
    .reduce((acc, c) => (c.draft ? [...acc, c.draft] : acc), []);
};

export const getDepartmentSpecificScriptContainer = (
  state: State,
): ?ApprovableContainer<DepartmentSpecificScript> =>
  state.service.departmentSpecificScripts.current;

export const getDepartmentSpecificScriptDraft = (state: State): ?DepartmentSpecificScript =>
  state.service.departmentSpecificScripts.draft;

export const getDepartmentSpecificScriptAssignments = (state: State): Array<HierarchyAssignment> =>
  state.service.departmentSpecificScripts.assignments;

export const getDepartmentSpecificScriptDraftAssignments = (
  state: State,
): Array<HierarchyAssignment> => state.service.departmentSpecificScripts.draftAssignments;

export const isDepartmentSpecificScriptDraftAssignmentsValid = (state: State): boolean => {
  const assignments = getDepartmentSpecificScriptDraftAssignments(state);
  return validateHierarchyAssignmentSchema(assignments);
};

export const isDepartmentSpecificScriptAssignmentsValid = (state: State): boolean => {
  const assignments = getDepartmentSpecificScriptAssignments(state);
  return validateHierarchyAssignmentSchema(assignments);
};

// MODALS
export const getShowAddDepartmentSpecificScriptModal = (state: State): boolean =>
  state.service.departmentSpecificScripts.modals.showAddDepartmentSpecificScriptModal;

export const getShowEditDepartmentSpecificScriptModal = (state: State): boolean =>
  state.service.departmentSpecificScripts.modals.showEditModal;

export const getDepartmentSpecificScriptsModalStep = (state: State): number =>
  state.service.departmentSpecificScripts.modals.modalStep;

export const getShowDepartmentSpecificScriptSubmitModal = (state: State): boolean =>
  state.service.departmentSpecificScripts.modals.showSubmitModal;

export const getShowDepartmentSpecificScriptCancelModal = (state: State): boolean =>
  state.service.departmentSpecificScripts.modals.showCancelModal;

export const getShowDepartmentSpecificScriptDiscardModal = (state: State): boolean =>
  state.service.departmentSpecificScripts.modals.showDiscardModal;

export const getShowDepartmentSpecificScriptCancelDiscardModal = (state: State): boolean =>
  state.service.departmentSpecificScripts.modals.showCancelDiscardModal;

export const getShowDepartmentSpecificScriptDisableModal = (state: State): boolean =>
  state.service.departmentSpecificScripts.modals.showDisableModal;

export const getShowDepartmentSpecificScriptEditAssignmentModal = (state: State): boolean =>
  state.service.departmentSpecificScripts.modals.showEditAssignmentsModal;
